import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Spinner, Tooltip } from '@patternfly/react-core';
import { PatternflyTable } from '@app/components/PatternflyTable';
import { sortable, cellWidth } from '@patternfly/react-table';
import { PageSection, Title, Modal, ModalVariant } from '@patternfly/react-core';
import { BullhornIcon, EllipsisVIcon } from '@patternfly/react-icons';
import { useControllerContext } from '@app/contexts/controller.context';
import { useDispatch, useSelector } from 'react-redux';
import { controllerSelectors } from '@app/common/configuration/configuration.selectors';
import { push } from 'connected-react-router';
import { useSearch } from '@app/lib/use-search';
import { Gateway } from '@app/types';
import { ErrorView } from '../error/error';
import { actionCreators } from '@app/common/configuration/configuration.reducer';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { MdArrowDownward, MdArrowUpward, MdCancel, MdCircle, MdError, MdExpandCircleDown, MdInfo, MdInfoOutline, MdOutlineErrorOutline, MdOutlineExpandCircleDown, MdUnfoldMore } from 'react-icons/md';
import { Button, Card, CardBody, CardHeader, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row } from 'reactstrap';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
  Column,
  Updater,
} from '@tanstack/react-table';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement } from 'chart.js';
import 'chartjs-adapter-moment';
import moment from 'moment';
import GoogleMapWithBubbles from '@app/components/GoogleMapWithBubbles';
import Loader from '@app/components/Loader';
import Swal from 'sweetalert2';
import Controller from '@app/api/controller/controller.actions';
import ControllerApi from '@app/api/controller-api/controller.actions';
import { reverseSortBy } from '@app/lib/functions';
import GatewayApi from '@app/api/gateway-api/gateway.actions';
import DashboardApi from '@app/api/dashboard-api/dashboard.actions';
import { useMapCache } from '@app/hooks/useMapCache';
import UserSession from '@app/common/user-session';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

// Status color mapping 
const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black
  1: "#ff0000", // Down - Red
  2: "#FBC02D", // Warning - Yellow
  3: "#45B649", // Good - Green
  4: "#808080"  // Missing - Gray
};

// Status text mapping for tooltips
const statusTexts: Record<number, string> = {
  0: "Offline - Controller has never been powered on",
  1: "Down - Controller is down", 
  2: "Degraded - One or more links are not connected",
  3: "Online - Controller is online and all links are connected",
  4: "Offline - Controller has been offline for more than 2 weeks"
};

// Status text mapping for gateway tooltips  
const gatewayStatusTexts: Record<number, string> = {
  0: "Offline - Gateway has never been powered on",
  1: "Down - Gateway is down", 
  2: "Degraded - One or more links are not connected",
  3: "Online - Gateway is online and all links are connected",
  4: "Offline - Gateway has been offline for more than 2 weeks"
};

const ControllerActionsMenu = ({ controller, basePath, isManager, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)} direction="start">
      <DropdownToggle
        color="link"
        className="list-actions-toggle"
        aria-label={`Actions for ${controller.name}`}
      >
        <EllipsisVIcon />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem tag={Link} to={`${basePath}/controller/edit/${controller.id}`}>
          Edit
        </DropdownItem>
        <DropdownItem disabled>Reboot</DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          className="list-action-danger"
          disabled={!isManager}
          onClick={() => onDelete(controller.id, controller.name)}
        >
          Delete
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const ControllerList: React.FC = () => {
  const dispatch = useDispatch();
  const { isLoading, isSuccess, isFailed } = useControllerContext();
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement);
  const [confirmOpen, setConfirmOpen] = React.useState({ isOpen: false, actionId: '' });
  const [expandedRows, setExpandedRows] = React.useState({});
  const [controllerStatuses, setControllerStatuses] = React.useState<Record<string, number>>({});
  const [statusLoading, setStatusLoading] = React.useState<Record<string, boolean>>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [gateways, setGateways] = React.useState<Record<string, any[]>>({});
  const [controllerDetails, setControllerDetails] = React.useState<Record<string, any>>({});
  const [gatewayStatuses, setGatewayStatuses] = React.useState<Record<string, number>>({});
  const [gatewayStatusLoading, setGatewayStatusLoading] = React.useState<Record<string, boolean>>({});
  
  const [sorting, setSorting] = React.useState([
    {
      id: 'name',
      desc: false,
    },
  ]);
  
  const allControllers = useSelector(controllerSelectors.selectAll);

  const basePath = process.env.REACT_APP_BASE_PATH || '';
  const isManager = UserSession.getParam('role')?.toLowerCase() === 'manager';

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const statuses = [
    "#85ba3b",
    "#e93a54",
    "#000"
  ];

  const generateRandomNumber = () => {
    const randomNumber = Math.floor(Math.random() * statuses.length);
    return statuses[randomNumber];
  }

  const enhanceDataWithDemo = (data) => {
    return data.map(item => ({
      ...item,
      status: generateRandomNumber(),
      gateway: item.gateway || [
        <>192.168.1.1</>,
        <>192.168.1.2</>,
        <>192.168.1.3</>,
      ],
    }));
  };

  React.useEffect(() => {
    (async function() {
      try {

        dispatch(actionCreators.controllerRequest());
        
        const [allGateways, allControllerDetails] = await Promise.all([
          GatewayApi.getAll(),
          ControllerApi.getAll().catch((error) => {
            console.error('Unable to load controller IP addresses:', error);
            return [];
          }),
        ]);
        
        // Group gateways by controller_id
        const gatewaysByController = allGateways.reduce((acc, gateway) => {
          if (gateway.controller_id) {
            if (!acc[gateway.controller_id]) {
              acc[gateway.controller_id] = [];
            }
            acc[gateway.controller_id].push(gateway);
          }
          return acc;
        }, {});
        
        setGateways(gatewaysByController);
        setControllerDetails((allControllerDetails || []).reduce((acc, controller) => {
          acc[controller.id] = controller;
          return acc;
        }, {}));
      }
      catch (error) {
        console.log(error);
      }
    })();
  }, [dispatch]);

  const fetchedControllers = React.useMemo(
    () => allControllers.filter((x) => x.name?.toLowerCase() !== 'serverless'),
    [allControllers]
  );

  const controllers = useSearch(fetchedControllers);
  const controllersData = controllers;

  const { controllers: cachedControllers, sites: cachedSites, dashboardData } = useMapCache();

  React.useEffect(() => {
    // Use cached controller statuses from MapDataContext
    const newStatuses: Record<string, number> = {};
    const newLoading: Record<string, boolean> = {};
    
    controllers.forEach(controller => {
      const cachedController = cachedControllers.find(c => c.id === controller.id);
      if (cachedController?.status !== undefined) {
        newStatuses[controller.id] = cachedController.status;
        newLoading[controller.id] = false;
      } else {
        
        newStatuses[controller.id] = 0; // Unknown status
        newLoading[controller.id] = false;
      }
    });
    
    setControllerStatuses(newStatuses);
    setStatusLoading(newLoading);
  }, [controllers, cachedControllers]); 

  // Get gateway statuses from centralized cache
  React.useEffect(() => {
    if (dashboardData?.partner?.gateways?.gateways) {
      const cachedGatewayStatuses = dashboardData.partner.gateways.gateways.reduce((acc, gateway) => {
        acc[gateway.id] = gateway.status;
        return acc;
      }, {} as Record<string, number>);
      
      setGatewayStatuses(cachedGatewayStatuses);
      
      const allGateways = Object.values(gateways).reduce<any[]>((acc, curr) => acc.concat(curr), []);
      setGatewayStatusLoading(prev => {
        const newLoading = { ...prev };
        allGateways.forEach(gateway => {
          newLoading[gateway.id] = false;
        });
        return newLoading;
      });
    }
  }, [dashboardData, gateways]);

  const columns = React.useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }: { row: any }) => {
        const hasGateways = gateways[row.original.id]?.length > 0;
        return (
          <>
            {hasGateways && (
              <button onClick={() => toggleRow(row.id)} className="table-arrow">
                {expandedRows[row.id] ? '▼' : '▶'}
              </button>
            )}
            <Link to={`${basePath}/controller/${row.original.id}`} className="controller-name" style={{ fontSize: '12px' }}>
              {row.original.name}
            </Link>
          </>
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.name || '';
        const bVal = rowB.original.name || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
      size: 150
    },
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '12px' }}>
          {row.original.id}
        </div>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.id || '';
        const bVal = rowB.original.id || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
      size: 200
    },
    {
      accessorKey: 'ipAddress',
      header: 'IP',
      accessorFn: (row) => (
        row.ip_address ||
        row.ipAddress ||
        controllerDetails[row.id]?.ip_address ||
        controllerDetails[row.id]?.ipAddress ||
        ''
      ),
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '12px' }}>
          {row.getValue('ipAddress') || 'N/A'}
        </div>
      ),
      size: 120
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '12px' }}>
          {row.original.description}
        </div>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.description || '';
        const bVal = rowB.original.description || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
      size: 200
    },
    {
      accessorKey: 'region',
      header: 'Location',
      accessorFn: (row) => {
        if (row.address?.city && row.address?.state) {
          return `${row.address.city}, ${row.address.state}`;
        }
        return row.address?.city || row.address?.country || '';
      },
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '12px' }}>
          {row.original.address?.city && row.original.address?.state 
            ? `${row.original.address.city}, ${row.original.address.state}` 
            : row.original.address?.city || row.original.address?.country || 'N/A'}
        </div>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.getValue(columnId) as string;
        const bVal = rowB.getValue(columnId) as string;
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
      size: 100
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableColumnFilter: false,
      accessorFn: (row) => {
        return controllerStatuses[row.id] ?? 0;
      },
      cell: ({ row }: { row: any }) => {
        const controllerId = row.original.id;
        if (statusLoading[controllerId]) {
          return <div className="status-column"><Spinner size="sm" /></div>;
        }
        const status = controllerStatuses[controllerId] ?? 0; // Default to 0 if undefined
        return (
          <div className="status-column">
            <div className="status-icon-container">
              <Tooltip content={statusTexts[status] || statusTexts[0]}>
                <MdCircle color={statusColors[status] || statusColors[0]} size={14} />
              </Tooltip>
            </div>
          </div>
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const statusA = controllerStatuses[rowA.original.id] ?? 0;
        const statusB = controllerStatuses[rowB.original.id] ?? 0;
        
        // Priority order: Black (0) → Red (1) → Yellow (2) → Gray (4) → Green (3)
        const priorityMap: Record<number, number> = {
          0: 0, // Black 
          1: 1, // Red 
          2: 2, // Yellow 
          4: 3, // Gray 
          3: 4, // Green 
        };
        
        const priorityA = priorityMap[statusA] ?? 0;
        const priorityB = priorityMap[statusB] ?? 0;
        
        return priorityA - priorityB;
      },
      size: 80
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => (
        <ControllerActionsMenu
          controller={row.original}
          basePath={basePath}
          isManager={isManager}
          onDelete={removeControllerAlert}
        />
      ),
      size: 60
    }
  ], [basePath, controllerStatuses, statusLoading, expandedRows, toggleRow, gateways, sorting, isManager, controllerDetails]);

  const table = useReactTable({
    data: controllersData,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
  });

  const removeControllerAlert = (controllerId, controllerName) => {
    Swal.fire({
      title: 'Confirm Action',
      html: `<div style="text-align: left;">
               <p>Are you sure you want to delete this controller?</p>
               <strong>Name:</strong> ${controllerName}
             </div>`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteController(controllerId);
      }
    });
  };

  const deleteController = React.useCallback((controllerId) => {
    dispatch(actionCreators.controllerDeleteRequest(controllerId));
  }, [dispatch]);

  // // Функция для генерации временных меток
  // const generateLabels = () => {
  //   const labels = [];
  //   const now = moment();

  //   for (let i = -4; i <= 4; i++) {
  //     labels.push(now.clone().add(i, 'hours').format('HH:mm'));
  //   }

  //   return labels;
  // };

  // // Генерация цвета для каждой линии
  // const generateRandomColor = () => {
  //   const r = Math.floor(Math.random() * 255);
  //   const g = Math.floor(Math.random() * 255);
  //   const b = Math.floor(Math.random() * 255);
  //   return `rgba(${r}, ${g}, ${b}, 0.5)`; // Полупрозрачный цвет для линий
  // };

  // const generateRandomChange = (min, max) =>  {
  //   return Math.random() * (max - min) + min;
  // }

  // // Генерация данных для каждого контроллера
  // const generateDatasets = (dataCollection, labels) => {
  //   return dataCollection.map((item, index) => ({
  //     label: item.name,
  //     data: labels.map(() => index + generateRandomChange(1,5)), // Случайные данные
  //     borderColor: generateRandomColor(), // Уникальный цвет для каждой линии
  //     fill: false, // Отключаем заливку под линией
  //     tension: 0.4,
  //     borderCapStyle: 'round',
  //     borderJoinStyle: 'round',
  //   }));
  // };

  // // Генерация данных для каждого контроллера
  // const generateDatasetsBandwidt = (dataCollection, labels) => {
  //   return dataCollection.map((item, index) => ({
  //     label: item.name,
  //     data: labels.map(() => generateRandomChange(0.5,3.5)), // Случайные данные
  //     borderColor: generateRandomColor(), // Уникальный цвет для каждой линии
  //     fill: false, // Отключаем заливку под линией
  //     tension: 0.4,
  //     borderCapStyle: 'round',
  //     borderJoinStyle: 'round',
  //   }));
  // };

  // const displayedData = table.getRowModel().rows.map(row => row.original);

  // const labels = generateLabels();

  // const chartData = {
  //   labels, // Метки времени
  //   datasets: generateDatasets(displayedData, labels), // Наборы данных
  // };

  // const bandwidthData = {
  //   labels, // Метки времени
  //   datasets: generateDatasetsBandwidt(displayedData, labels), // Наборы данных
  // };

  // const chartOptions = (theme) => ({
  //   scales: {
  //     x: {
  //       ticks: {
  //         color: theme === 'dark' ? '#fff' : '#000',
  //       },
  //       grid: {
  //         color: theme === 'dark' ? '#444' : '#ddd',
  //       },
  //     },
  //     y: {
  //       min: 0,
  //       max: 20,
  //       ticks: {
  //         color: theme === 'dark' ? '#fff' : '#000',
  //         callback: function (value) {
  //           return value + '%';
  //         },
  //       },
  //       grid: {
  //         color: theme === 'dark' ? '#444' : '#ddd',
  //       },
  //     },
  //   },
  //   plugins: {
  //     legend: {
  //       display: true,
  //       position: 'top' as const,
  //       labels: {
  //         font: {
  //           size: 14,
  //         },
  //         color: theme === 'dark' ? '#fff' : '#000',
  //       },
  //     },
  //     beforeDraw: (chart) => {
  //       const ctx = chart.ctx;
  //       ctx.save();
  //       ctx.fillStyle = theme === 'dark' ? '#000' : '#fff';
  //       ctx.fillRect(0, 0, chart.width, chart.height);
  //       ctx.restore();
  //     },
  //   },
  // });

  // const bandwidthOptions = (theme) => ({
  //   scales: {
  //     x: {
  //       ticks: {
  //         color: theme === 'dark' ? '#fff' : '#000',
  //       },
  //       grid: {
  //         color: theme === 'dark' ? '#444' : '#ddd',
  //       },
  //     },
  //     y: {
  //       min: 0,
  //       max: 5,
  //       ticks: {
  //         color: theme === 'dark' ? '#fff' : '#000',
  //         callback: function (value) {
  //           return value + 'Mb/s';
  //         },
  //       },
  //       grid: {
  //         color: theme === 'dark' ? '#444' : '#ddd',
  //       },
  //     },
  //   },
  //   plugins: {
  //     legend: {
  //       display: true,
  //       position: 'top' as const,
  //       labels: {
  //         font: {
  //           size: 14,
  //         },
  //         color: theme === 'dark' ? '#fff' : '#000',
  //       },
  //       beforeDraw: (chart) => {
  //         const ctx = chart.ctx;
  //         ctx.save();
  //         ctx.fillStyle = theme === 'dark' ? '#000' : '#fff';
  //         ctx.fillRect(0, 0, chart.width, chart.height);
  //         ctx.restore();
  //       },
  //     },
  //   },
  // });
  // //end new

  if (isFailed) {
    return (
      <React.Fragment>
        <Helmet>
          <title>Controllers</title>
        </Helmet>
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 controllers-list ${theme}`}
          title=""
          breadcrumbs={[{ name: 'Controllers', active: true }]}
          actionButton={
            <Link to={`${basePath}/controller/new`}>
              <Button className="ethica-button-green" size="sm">Add Controller</Button>
            </Link>
          }
        >
          <div className="d-flex justify-content-end mb-3">
            <Link to={`${basePath}/controller/new`}>
              <Button className="ethica-button-green" size="sm">Add Controller</Button>
            </Link>
          </div>
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper">
                <Card className="p-3">
                  <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>ID</th>
                        <th>IP</th>
                        <th>Description</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={7} className="text-center">No data available</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-end mt-3">
                    <Link to={`${basePath}/controller/new`}>
                      <Button className="ethica-button-green" size="sm">Add Controller</Button>
                    </Link>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </Page>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Controllers</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 controllers-list ${theme}`}
          title=""
          breadcrumbs={[{ name: 'Controllers', active: true }]}
          actionButton={
            <Link to={`${basePath}/controller/new`}>
              <Button className="ethica-button-green" size="sm">Add Controller</Button>
            </Link>
          }
        >
          <div className="d-flex justify-content-end mb-3">
            <Link to={`${basePath}/controller/new`}>
              <Button className="ethica-button-green" size="sm">Add Controller</Button>
            </Link>
          </div>
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper">
                <Card className="p-3">
                  <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th 
                              key={header.id} 
                              colSpan={header.colSpan}
                              style={{ 
                                padding: '8px',
                                whiteSpace: 'nowrap',
                                textAlign: header.column.getCanFilter() ? 'left' : 'center'
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div className={`d-flex align-items-center ${header.column.getCanFilter() ? 'justify-content-start' : 'justify-content-center'}`}>
                                  <div
                                    {...{
                                      className: header.column.getCanSort()
                                        ? 'select-none sites-header'
                                        : 'sites-header',
                                      onClick: header.column.getToggleSortingHandler(),
                                    }}
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}

                                    {header.column.getCanSort() && (
                                      {
                                        asc: <MdArrowUpward className="ms-2" size={14} />,
                                        desc: <MdArrowDownward className="ms-2" size={14} />,
                                      }[header.column.getIsSorted() as string] ?? (
                                        <MdUnfoldMore className="ms-2 opacity-50" size={14} />
                                      )
                                    )}
                                  </div>
                                  {header.column.getCanFilter() ? (
                                    <div className="d-flex justify-content-end">
                                      <Filter column={header.column} />
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <React.Fragment key={row.id}>
                          <tr>
                            {row.getVisibleCells().map(cell => (
                              <td 
                                key={cell.id} 
                                style={{ 
                                  padding: '8px',
                                  fontSize: '14px',
                                  textAlign: cell.column.columnDef.header === 'Description' ? 'left' : (cell.column.getCanFilter() ? 'left' : 'center'),
                                  whiteSpace: 'nowrap',
                                  overflow: cell.column.id === 'actions' ? 'visible' : 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: cell.column.columnDef.size || 'auto',
                                  position: cell.column.id === 'actions' ? 'relative' : undefined,
                                  zIndex: cell.column.id === 'actions' ? 2 : undefined,
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                          {expandedRows[row.id] && gateways[row.original.id] && (
                            <tr>
                              <td colSpan={7} style={{ padding: '0', borderTop: '1px solid #dee2e6' }}>
                                <div style={{ margin: '0', padding: '10px 0', borderBottom: 'none', width: '100%' }}>
                                  <div style={{ 
                                    display: 'flex',
                                    width: '100%',
                                    paddingLeft: '34px',
                                    borderBottom: '1px solid #dee2e6',
                                    backgroundColor: '#f8f9fa'
                                  }}>
                                    <div style={{ 
                                      width: '30%',
                                      padding: '8px',
                                      fontSize: '12px',
                                      color: '#333',
                                      fontWeight: '600',
                                      textAlign: 'left'
                                    }}>
                                      ID
                                    </div>
                                    <div style={{ 
                                      width: '20%',
                                      padding: '8px',
                                      fontSize: '12px',
                                      color: '#333',
                                      fontWeight: '600',
                                      textAlign: 'center'
                                    }}>
                                      IP
                                    </div>
                                    <div style={{ 
                                      width: '15%',
                                      padding: '8px',
                                      fontSize: '12px',
                                      color: '#333',
                                      fontWeight: '600',
                                      textAlign: 'center'
                                    }}>
                                      Status
                                    </div>
                                    <div style={{ 
                                      width: '35%',
                                      padding: '8px'
                                    }}></div>
                                  </div>
                                  {gateways[row.original.id].map((gateway, index, array) => (
                                    <div 
                                      key={index} 
                                      className="expanded-gateway-row"
                                      style={{ 
                                        display: 'flex',
                                        width: '100%',
                                        paddingLeft: '34px',
                                        borderTop: index === 0 ? 'none' : '1px solid #dee2e6'
                                      }}
                                    >
                                      <div style={{ 
                                        width: '30%',
                                        padding: '8px',
                                        fontSize: '12px',
                                        color: '#222222',
                                        textAlign: 'left'
                                      }}>
                                        {gateway.id}
                                      </div>
                                      <div style={{ 
                                        width: '20%',
                                        padding: '8px',
                                        fontSize: '12px',
                                        color: '#222222',
                                        textAlign: 'center'
                                      }}>
                                        {gateway.ip_4_address}
                                      </div>
                                      <div style={{ 
                                        width: '15%',
                                        padding: '8px',
                                        textAlign: 'center'
                                      }}>
                                        {gatewayStatusLoading[gateway.id] ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <div className="status-icon-container">
                                            <Tooltip content={gatewayStatusTexts[gatewayStatuses[gateway.id] || 0] || gatewayStatusTexts[0]}>
                                              <MdCircle 
                                                color={statusColors[gatewayStatuses[gateway.id] || 0]} 
                                                size={14} 
                                              />
                                            </Tooltip>
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ 
                                        width: '35%',
                                        padding: '8px'
                                      }}></div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  {/* Пагинация */}
                  <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
                    <div className="me-2">
                      <span className="flex items-center gap-1">
                        Go to page:
                        <input
                          type="number"
                          min="1"
                          max={table.getPageCount()}
                          defaultValue={table.getState().pagination.pageIndex + 1}
                          onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0
                            table.setPageIndex(page)
                          }}
                          className="border p-1 rounded w-16"
                        />
                      </span>
                    </div>
                    <div>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.setPageIndex(0)}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<<'}
                        </button>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.previousPage()}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<'}
                        </button>
                        <span className="flex items-center me-1">
                          <strong>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
                          </strong>
                        </span>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.nextPage()}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>'}
                        </button>
                        <button
                          className="border rounded p-1 me-2"
                          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>>'}
                        </button>
                      </div>
                      <div>
                        <select
                          className="form-select"
                          value={table.getState().pagination.pageSize}
                          onChange={e => {
                            table.setPageSize(Number(e.target.value))
                          }}
                        >
                          {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                              Show {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>

          {/* <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper controller-performance justify-content-between">
                <h2><strong>Controllers Performance</strong></h2>
                <div className="d-flex flex-nowrap justify-content-between w-100">
                  <Col lg={6} md={6} sm={12} xs={12} className="me-1">
                      <Card>
                        <CardHeader>
                          CPU
                        </CardHeader>
                        <CardBody className="p-2">
                        <Line data={chartData} options={chartOptions(theme)}
                          style={{
                            backgroundColor: theme === 'dark' ? '#333030' : '#fff',
                          }}/>
                        </CardBody>
                      </Card>
                  </Col>

                  <Col lg={6} md={6} sm={12} xs={12}>
                    <Card>
                      <CardHeader>
                        Bandwidth
                      </CardHeader>
                      <CardBody className="p-2">
                        <Line data={bandwidthData} options={bandwidthOptions(theme)}/>
                      </CardBody>
                    </Card>
                  </Col>
                </div>
              </div>
            </Col>
          </Row> */}

          <Row>
            <Col lg={12} md={12} sm={12} xs={12}>
              <div className="controllers-card-wrapper controller-map">
                <h2><strong>Controllers Map</strong></h2>
                <Card inverse style={{background: '#808080'}}>
                  <GoogleMapWithBubbles />
                </Card>
              </div>
            </Col>
          </Row>
        </Page>
      )}
    </React.Fragment>
  );
};

function setColumnFilters(updaterOrValue: Updater<ColumnFiltersState>): void {
  throw new Error('Function not implemented.');
}

interface CustomColumnMeta {
  filterVariant?: 'range' | 'select' | 'text';
}

function Filter({ column }: { column: Column<any, unknown> & { columnDef: { meta?: CustomColumnMeta } } }): JSX.Element {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
